import { Link, useLoaderData } from "react-router";
import type { LoaderFunctionArgs } from "react-router";
import { articleJsonLd } from "@/lib/jsonld";
import { buildMetadata } from "@/lib/seo";
import type { Story } from "@/data/stories";

import type { SiteContent } from "@/content";

import { ArrowRight, ButtonLink } from "@/components/ui/Button";
import { StoryCard } from "@/components/ui/Cards";
import { JsonLd } from "@/components/ui/JsonLd";
import { MediaFigure } from "@/components/ui/MediaFigure";
import { PageHeader } from "@/components/ui/PageHeader";
import { Reveal, RevealGroup } from "@/components/ui/Reveal";
import { Section, SectionHeading } from "@/components/ui/Section";
import { imageReveal } from "@/lib/motion";

type LoaderData = { story: Story; others: Story[] };

/**
 * The story body is not in the bootstrap payload, so this route fetches its own
 * document. An unknown slug throws a 404 Response rather than rendering an
 * empty page, which is what notFound() did before and what the audit expects.
 */
export async function loader({ params, context, request }: LoaderFunctionArgs): Promise<LoaderData> {
  const { fetchStory, fetchSiteContent } = await import("@/content");

  // On the server the SSR handler has already loaded the content and passes it
  // through context. In the browser, after following a link, there is no
  // context, and assuming there was one is what sent every client-side
  // navigation to a detail page straight into the error boundary.
  const content = (context as { content?: SiteContent } | undefined)?.content ?? (await fetchSiteContent(request.signal));

  const story = await fetchStory(String(params.slug), request.signal);
  if (!story) throw new Response("No story with that slug.", { status: 404 });

  return { story, others: content.stories.filter((s) => s.slug !== story.slug).slice(0, 3) };
}

export const meta = ({ data }: { data?: LoaderData }) =>
  data
    ? buildMetadata({
        title: data.story.title,
        description: data.story.excerpt,
        path: `/stories/${data.story.slug}`,
        type: "article",
        publishedTime: data.story.isoDate,
      })
    : null;

export function StoryPage() {
  const { story, others } = useLoaderData<LoaderData>();

  return (
    <>
      <PageHeader
        eyebrow={story.pillar}
        title={story.title}
        lead={story.excerpt}
        trail={[
          { name: "Home", href: "/" },
          { name: "Stories", href: "/stories" },
          { name: story.title, href: `/stories/${story.slug}` },
        ]}
      />

      <Section>
        <p className="text-[0.9375rem] text-muted">
          <time dateTime={story.isoDate}>{story.date}</time>
          <span aria-hidden="true"> · </span>
          {story.location}
          <span aria-hidden="true"> · </span>
          {story.readingTime}
        </p>

        <Reveal variants={imageReveal} className="mt-8">
          <MediaFigure
            item={story.image}
            ratio="16 / 9"
            sizes="(min-width: 1280px) 1180px, 100vw"
          />
        </Reveal>

        <div className="mt-14 grid gap-12 lg:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)] lg:gap-16">
          <Reveal>
            <article className="max-w-2xl">
              {story.body.map((block) => (
                <section key={block.heading ?? block.paragraphs[0].slice(0, 40)} className="mb-10 last:mb-0">
                  {block.heading ? (
                    <h2 className="mb-4" style={{ fontSize: "var(--step-h3)" }}>
                      {block.heading}
                    </h2>
                  ) : null}
                  <div className="prose-body text-ink" style={{ fontSize: "var(--step-lead)" }}>
                    {block.paragraphs.map((paragraph) => (
                      <p key={paragraph.slice(0, 40)}>{paragraph}</p>
                    ))}
                  </div>
                </section>
              ))}
            </article>
          </Reveal>

          <Reveal>
            {story.facts && story.facts.length > 0 ? (
              <div className="rounded-[16px] border border-line bg-mist p-6">
                <h2 className="text-[0.8125rem] font-bold uppercase tracking-[0.14em] text-blue">
                  Reported figures
                </h2>
                <dl className="mt-4 space-y-3">
                  {story.facts.map((fact) => (
                    <div
                      key={fact.label}
                      className="border-b border-line pb-3 last:border-0 last:pb-0"
                    >
                      <dt className="text-[0.875rem] text-muted">{fact.label}</dt>
                      <dd className="font-serif text-[1.0625rem] text-midnight">{fact.value}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            ) : null}

            <div className="mt-6 rounded-[16px] border border-line bg-white p-6">
              <h2 className="text-[0.8125rem] font-bold uppercase tracking-[0.14em] text-blue">
                Programme
              </h2>
              <p className="mt-3">
                {story.pillarSlug === "get-involved" ? (
                  <Link to="/get-involved" className="font-semibold text-navy underline underline-offset-4">
                    {story.pillar}
                  </Link>
                ) : (
                  <Link
                    to={`/our-work/${story.pillarSlug}`}
                    className="font-semibold text-navy underline underline-offset-4"
                  >
                    {story.pillar}
                  </Link>
                )}
              </p>
              <p className="mt-6 border-t border-line pt-5 text-[0.8125rem] leading-relaxed text-muted">
                Source:{" "}
                <a
                  href={story.source}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="text-navy underline underline-offset-4"
                >
                  himshikharanepal.org.np
                </a>
              </p>
            </div>
          </Reveal>
        </div>
      </Section>

      <Section tone="mist" ariaLabelledBy="more-stories-heading">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <SectionHeading id="more-stories-heading" eyebrow="Keep reading" title="More stories" />
          <ButtonLink href="/stories" variant="secondary">
            View all stories
            <ArrowRight />
          </ButtonLink>
        </div>
        <RevealGroup as="ul" className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {others.map((other) => (
            <Reveal as="li" key={other.slug}>
              <StoryCard story={other} />
            </Reveal>
          ))}
        </RevealGroup>
      </Section>

      <JsonLd
        data={articleJsonLd({
          title: story.title,
          description: story.excerpt,
          path: `/stories/${story.slug}`,
          isoDate: story.isoDate,
        })}
      />
    </>
  );
}
